import { Button, Typography, InputLabel, TextArea, Icon } from '@scale/scaleui';
import produce from 'immer';
import { NamedEntityRecognitionStepParams } from '@scale/shared/types/chat';
import { useEffect, useState } from 'react';
import {
  INamedEntityRecognitionAttribute,
  NamedEntityRecognitionAttributeType,
} from '@scale/shared/types/params/namedentityrecognition';
import { isEqual, keys, uniqueId } from 'lodash';

const DEFAULT_DISPLAY_NAME = 'Display Name';
const DEFAULT_DESCRIPTION = 'Enter a description here';

export function AttributePerNERConfig({
  currentParams,
  setCurrentParams,
}: {
  currentParams: NamedEntityRecognitionStepParams;
  setCurrentParams: (value: any) => void;
}) {
  const [attributePerNER, setAttributePerNER] = useState<
    Record<string, INamedEntityRecognitionAttribute>
  >(currentParams.params.default_attribute_fields ?? {});

  const disableSave =
    isEqual(attributePerNER, currentParams.params.default_attribute_fields) ||
    (currentParams.params.default_attribute_fields === undefined &&
      keys(attributePerNER).length === 0);

  useEffect(() => {
    if (!disableSave) {
      setCurrentParams(
        produce(currentParams, draft => {
          draft.params.default_attribute_fields = attributePerNER;
        }),
      );
    }
  }, [disableSave, attributePerNER, currentParams, setCurrentParams]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <Typography.Subtitle3>Attribute Per NER</Typography.Subtitle3>
        <Typography.Subtitle4>Add attributes on each NER tag</Typography.Subtitle4>
      </div>

      <div className="flex flex-col gap-2">
        {keys(attributePerNER).map(id => (
          <div key={id} className="flex items-center gap-2">
            <div className="flex-1 border border-neutral-200 shadow-sm rounded-md px-4 py-3">
              <div className="flex flex-col gap-2">
                <div>
                  <div className="flex items-start w-full">
                    <InputLabel required>Display Name</InputLabel>
                  </div>
                  <TextArea
                    rows={1}
                    className="w-full"
                    value={attributePerNER[id].display_name}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setAttributePerNER(prev =>
                        produce(prev, draft => {
                          draft[id].display_name = e.target.value;
                        }),
                      );
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-start w-full">
                    <InputLabel>Description</InputLabel>
                  </div>
                  <TextArea
                    rows={1}
                    className="w-full"
                    value={attributePerNER[id].description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setAttributePerNER(prev =>
                        produce(prev, draft => {
                          draft[id].description = e.target.value;
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <Icon
              hasBackground
              size="sm"
              icon="trash"
              variant="danger"
              onClick={() =>
                setAttributePerNER(prev =>
                  produce(prev, draft => {
                    if (id in draft) {
                      delete draft[id];
                    }
                  }),
                )
              }
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() =>
            setAttributePerNER(prev => ({
              ...prev,
              [uniqueId()]: {
                type: NamedEntityRecognitionAttributeType.Text,
                display_name: DEFAULT_DISPLAY_NAME,
                description: DEFAULT_DESCRIPTION,
              },
            }))
          }
        >
          Add Attribute
        </Button>
      </div>
    </div>
  );
}
